import type { TrackedTranslation } from '@ps/web-core-intl';
import type { InputProps } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { useField } from 'react-final-form';

import { setSelectionRange } from '@swf/core/utils/formatting';

import useAutoFocusInput from '../../hooks/useAutoFocusIf';
import TextInput from '../TextInput/TextInput';

interface Props extends InputProps {
  label: TrackedTranslation;
  dateFormat?: string;
  hideFormatInLabel?: boolean;
  hidePlaceholder?: boolean;
  autocompletePattern?: 'off' | 'cc-exp' | 'none';
}

const DateInput: React.FunctionComponent<Props> = ({
  label,
  dateFormat,
  placeholder,
  hidePlaceholder,
  hideFormatInLabel,
  autocompletePattern = 'off',
  ...props
}) => {
  const {
    input: { value }
  } = useField(props.id, {
    subscription: { value: true }
  });
  const [focused, setFocused] = useState(false);
  const inputEl = useAutoFocusInput(!!props.autoFocus);
  let currentSelection = 0;

  if (inputEl.current?.selectionStart) {
    currentSelection = inputEl.current.selectionStart;
  }

  useEffect(() => {
    const { current } = inputEl;
    if (!dateFormat) return;
    if (!current) {
      return;
    }
    if (focused) {
      setSelectionRange(dateFormat, current, currentSelection);
    }
  }, [currentSelection, dateFormat, focused, inputEl, value]);

  const message = `${label.defaultMessage}${
    hideFormatInLabel || !dateFormat ? '' : ' (' + dateFormat + ')'
  }`;

  return (
    <TextInput
      {...props}
      type="tel"
      inputMode="numeric"
      inputRef={inputEl}
      label={{
        ...label,
        defaultMessage: message
      }}
      onFocus={() => setFocused(true)}
      placeholder={hidePlaceholder ? '' : placeholder || dateFormat}
      numericKeyboard
      autoComplete={autocompletePattern}
      checkDisabled
    />
  );
};

export default DateInput;
